// Importing necessary modules and libraries
import { useAuthStore } from '../store/auth';
import axios from './axios';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

// Configuring global toast notifications using Swal
const Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

// Error Toast Helper
const showErrorToast = (message) => {
    Toast.fire({
        icon: 'error',
        title: message,
    });
};

// Function to handle user login
export const login = async (email, password) => {
    try {
        const { data, status } = await axios.post('user/token/', {
            email,
            password,
        });

        if (status === 200) {
            setAuthUser(data.access, data.refresh);
            Toast.fire({
                icon: 'success',
                title: 'Signed in successfully',
            });
        }

        return { data, error: null };
    } catch (error) {
        showErrorToast(error.response?.data?.detail || 'Something went wrong');
        return { data: null, error: error.response.data?.detail || 'Something went wrong' };
    }
};

// Function to handle user registration
export const register = async (full_name, email, phone, password, password2) => {
    try {
        const { data } = await axios.post('user/register/', {
            full_name,
            email,
            phone,
            password,
            password2,
        });

        await login(email, password);  // Log in the newly registered user
        Toast.fire({
            icon: 'success',
            title: 'Signed Up Successfully',
        });

        return { data, error: null };
    } catch (error) {
        showErrorToast(error.response?.data || 'Something went wrong');
        return { data: null, error: error.response?.data || 'Something went wrong' };
    }
};

// Function to handle user logout
export const logout = () => {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    useAuthStore.getState().setUser(null);
    Toast.fire({
        icon: 'success',
        title: 'You have been logged out.',
    });
};

// Function to check if the access token is expired
export const isAccessTokenExpired = (accessToken) => {
    try {
        const decodedToken = jwtDecode(accessToken);
        const expirationDate = decodedToken.exp * 1000; // Convert to milliseconds
        return Date.now() > expirationDate;
    } catch (err) {
        return true;  // Return true if token is invalid or expired
    }
};

// Function to get a new access token using the refresh token
export const getRefreshToken = async () => {
    const refresh_token = Cookies.get('refresh_token');
    if (!refresh_token) {
        throw new Error('No refresh token found');
    }

    try {
        const response = await axios.post('user/token/refresh/', {
            refresh: refresh_token,
        });
        return response.data;
    } catch (error) {
        // Handle specific error cases
        if (error.response?.status === 401) {
            if (error.response?.data?.detail === 'Token is blacklisted') {
                // Clear invalid tokens and redirect to login
                Cookies.remove('access_token');
                Cookies.remove('refresh_token');
                useAuthStore.getState().setUser(null);
                
                // Show user-friendly message
                Swal.fire({
                    icon: 'warning',
                    title: 'Session Expired',
                    text: 'Your session has expired. Please login again.',
                    confirmButtonText: 'Login',
                }).then(() => {
                    // Redirect to login page
                    window.location.href = '/login';
                });
                
                throw new Error('Token is blacklisted - please login again');
            } else {
                throw new Error('Invalid refresh token - please login again');
            }
        }
        
        showErrorToast(error.response?.data?.detail || 'Failed to refresh token');
        throw new Error(error.response?.data?.detail || 'Failed to refresh token');
    }
};

// Function to set the authenticated user in the application state
export const setAuthUser = (access_token, refresh_token) => {
    // Set tokens in cookies with expiration based on environment (secure in production)
    Cookies.set('access_token', access_token, {
        expires: 7,  // Access token expires in 7 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',  // Changed to lax for better compatibility
        path: '/'  // Ensure cookie is available site-wide
    });

    Cookies.set('refresh_token', refresh_token, {
        expires: 30,  // Refresh token expires in 30 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',  // Changed to lax for better compatibility
        path: '/'  // Ensure cookie is available site-wide
    });

    // Decode the access token to get user info
    try {
        const user = jwtDecode(access_token);
        if (user) {
            useAuthStore.getState().setUser(user);
        }
    } catch (error) {
        console.error('Failed to decode access token:', error);
        // Clear invalid tokens
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        useAuthStore.getState().setUser(null);
    }

    useAuthStore.getState().setLoading(false);
};

// Function to check if user session is still valid
export const isSessionValid = () => {
    const accessToken = Cookies.get('access_token');
    const refreshToken = Cookies.get('refresh_token');
    
    if (!accessToken || !refreshToken) {
        return false;
    }
    
    try {
        const user = jwtDecode(accessToken);
        const now = Date.now() / 1000;
        
        // Check if token is expired or will expire soon (within 1 hour)
        if (user.exp < now + (60 * 60)) {
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Failed to decode access token:', error);
        return false;
    }
};

// Function to extend user session if needed
export const extendSession = async () => {
    const accessToken = Cookies.get('access_token');
    const refreshToken = Cookies.get('refresh_token');
    
    if (!accessToken || !refreshToken) {
        return false;
    }
    
    try {
        // Check if token is expired or will expire soon (within 1 day)
        const user = jwtDecode(accessToken);
        const now = Date.now() / 1000;
        
        if (user.exp < now + (24 * 60 * 60)) {
            const response = await getRefreshToken();
            setAuthUser(response.access, response.refresh);
            return true;
        }
        
        return true;
    } catch (error) {
        console.error('Failed to extend session:', error);
        return false;
    }
};

// Function to set the user during page load based on cookies
export const setUser = async () => {
    const accessToken = Cookies.get('access_token');
    const refreshToken = Cookies.get('refresh_token');

    // If there are no tokens, skip user setup
    if (!accessToken || !refreshToken) {
        useAuthStore.getState().setLoading(false);
        return;
    }

    // If the access token is expired, try to refresh it
    if (isAccessTokenExpired(accessToken)) {
        try {
            const response = await getRefreshToken();
            setAuthUser(response.access, response.refresh);
        } catch (error) {
            console.error('Token refresh failed:', error);
            // Clear invalid tokens
            Cookies.remove('access_token');
            Cookies.remove('refresh_token');
            useAuthStore.getState().setUser(null);
            useAuthStore.getState().setLoading(false);
        }
    } else {
        // Access token is still valid, set user directly
        try {
            const user = jwtDecode(accessToken);
            if (user) {
                useAuthStore.getState().setUser(user);
                
                // Set up automatic token refresh before expiration (refresh 1 day before expiry)
                const timeUntilExpiry = (user.exp * 1000) - Date.now();
                const refreshTime = Math.max(timeUntilExpiry - (24 * 60 * 60 * 1000), 60000); // Refresh 1 day before expiry, minimum 1 minute
                
                
                setTimeout(async () => {
                    try {
                        const response = await getRefreshToken();
                        setAuthUser(response.access, response.refresh);
                    } catch (error) {
                        console.error('Automatic token refresh failed:', error);
                        // Don't clear tokens on refresh failure, let user continue with current session
                    }
                }, refreshTime);
            }
        } catch (error) {
            console.error('Failed to decode access token:', error);
            // Clear invalid tokens
            Cookies.remove('access_token');
            Cookies.remove('refresh_token');
            useAuthStore.getState().setUser(null);
        } finally {
            useAuthStore.getState().setLoading(false);
        }
    }
};
